import { FoodHeader } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';

/**
 * Collapses repeats out of the recent-meals list, keeping the newest of each repeat.
 *
 * People eat the same thing over and over, so without this a fortnight of logs shows the same
 * row several times and pushes everything else out of sight. The newest copy wins because it is
 * the one whose date is worth reading.
 *
 * **A repeat means the same foods in the SAME meal slot.** Eating the same foods for Meal 1 and
 * for Meal 2 is not a repeat — those are two different meals that happen to hold the same
 * things, and each one is the last time that slot was used. Matching on the foods alone broke
 * the meal screen's "repeat your last meal" offer; `design-docs/nutrition-logging-routes.md`,
 * "A repeat is the same foods in the SAME slot", says how (#4969).
 *
 * The caller sorts newest-first and this relies on it, because "which one survives" is decided
 * by arrival order. Everything else is treated as untrustworthy: these come from stored
 * documents that older versions of the app wrote, so a meal that is missing, or has no name,
 * counts as "slot unknown" instead of throwing. Slot-unknown is a slot of its own — such a meal
 * can collapse with another slot-unknown meal holding the same foods, and never with a meal that
 * really is in Meal 1.
 */
export function dropRepeatedRecentMeals(headers: FoodHeader[]): FoodHeader[] {
  const seen = new Set<string>();
  return headers.filter(header => {
    const slot = (header.cachedFood as Log_Meal | undefined)?.name;
    // a Set rather than a plain object used as a map: with an object, a description of
    // `constructor` or `__proto__` reads as already-seen off the prototype, and the meal
    // silently disappears from the list.
    const key = JSON.stringify([typeof slot === 'string' ? slot : null, header.description ?? null]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
